import React, { useCallback } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { ReferenceLoader } from '@atlassian/jira-bulk-operations-common/src/utils/reference-loader/use-reference-loader/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraDetailPanel } from '@atlassian/jira-layout-controller/src/ui/detail-panel/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { getIsSoftwareThemingM2Enabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { Resizer } from '../detail-view/resizer/view.tsx';

type Props = {
	parentWidth: number;
	isOpen: boolean;
	bulkOperationsForm: ReferenceLoader;
};

export const BulkOperationsSidebar = ({ parentWidth, isOpen, bulkOperationsForm }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSoftwareFullTheming } = getIsSoftwareThemingM2Enabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };

	const fireResizeAnalytics = useCallback(
		(ratio: number) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'resized',
				actionSubject: 'bulkMenu',
			});
			fireUIAnalytics(analyticsEvent, 'bulkEdit', { width: ratio });
		},
		[createAnalyticsEvent],
	);

	if (!isOpen) return null;

	return (
		<JiraDetailPanel>
			{(setDetailPanelWidth) => (
				<Resizer
					parentWidth={parentWidth}
					onResize={setDetailPanelWidth}
					onResizeEnd={fireResizeAnalytics}
				>
					<Box
						xcss={[
							fg('bulk_transitions_required_unavailable_field_screen')
								? sidebarContainerStyle
								: sidebarContainerStyleOld,
							isSoftwareFullTheming && themedContainerStyles,
						]}
						ref={bulkOperationsForm.setRef}
						testId="software-backlog.bulk-operations-sidebar.bulkops-sidebar"
					/>
				</Resizer>
			)}
		</JiraDetailPanel>
	);
};

const sidebarContainerStyleOld = xcss({
	width: `calc(100% - ${token('space.100')})`,
});

const sidebarContainerStyle = xcss({
	width: `calc(100% - ${token('space.100')})`,
	height: '100%',
});

const themedContainerStyles = xcss({
	borderRadius: 'border.radius.100',
	backgroundColor: 'elevation.surface.raised',
	boxShadow: 'elevation.shadow.overlay',
});
